import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from "react-router-dom";
import {
    ChevronUp, ChevronDown, ChevronLeft, ChevronRight,
    Star, Truck, RotateCcw, Minus, Plus, ArrowRight, ArrowLeft,
    Check,
} from 'lucide-react';
import { useAppStore, useCheckoutStore } from "../../store/Store"
import pushToDataLayer from '../../apis/GTM.js';
import { getTrackingMetadata, getManualTrackingMetadata } from '../../apis/TrackingMetaData.js';
import CartIcon from '../../icons/CartIcon.jsx';
import Box from '@mui/material/Box';
import axios from 'axios';
import { BASE_URL } from '../../apis/constant';
import RelatedProduct from '../RelatedProduct.jsx';
import SocialButtons from '../SocialButtons.jsx';



const ProductSection = () => {
    const { addToCart, initializeCart } = useAppStore();
    const { setCheckoutItems, setShowAddToCartModal } = useCheckoutStore();
    const [productData, setProductData] = useState({
        id: '',
        name: '',
        description: '',
        price: 0,
        discountPrice: null,
        currency: '৳',
        rating: 0,
        reviewCount: 0,
        features: [],
        images: [],
        colors: [],
        sizes: [],
        badges: [],
        hasVariants: false,
        loading: true
    });


    const [selectedColor, setSelectedColor] = useState(null);
    const [selectedSize, setSelectedSize] = useState(null);
    const [selectedVariant, setSelectedVariant] = useState(null);
    const [quantity, setQuantity] = useState(1);
    const [activeImage, setActiveImage] = useState(null);
    const [isAddingToCart, setIsAddingToCart] = useState(false);

    const scrollRef = useRef(null);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);

    const [blinkSelector, setBlinkSelector] = useState(false);
    const [shakeSelector, setShakeSelector] = useState(false);
    const colorSelectorRef = useRef(null);
    const sizeSelectorRef = useRef(null);

    const [isModalOpen, setIsModalOpen] = useState(true);

    const navigate = useNavigate();
    const { id } = useParams();


    // Check variant types
    const hasColorVariants = productData.hasVariants && productData.colors && productData.colors.length > 0;
    const hasSizeVariants = productData.hasVariants && productData.sizes && productData.sizes.length > 0;

    // Determine which selector to show
    const shouldShowColorSelector = hasColorVariants;
    const shouldShowSizeSelector = hasSizeVariants;


    // Fetch product data
    const hasTrackedViewContent = useRef(false);
    useEffect(() => {
        const fetchProductData = async () => {
            setProductData(prev => ({ ...prev, loading: true }));

            const tracking_data = getTrackingMetadata();

            try {
                const response = await axios.post(`${BASE_URL}/api/get_product_page_data/`,
                    { product_id: id, tracking_data });
                const data = response.data;
                setProductData({ ...data, loading: false });

                /* ------------------------ GTM Configuration ------------------------ */
                if (!hasTrackedViewContent.current) {
                    pushToDataLayer('view_content',
                        {
                            event_id: tracking_data.event_id,
                            value: data.discountPrice || data.price,
                            currency: 'BDT',
                            content_name: data.name,
                            content_ids: [String(data.id)],
                            content_type: 'product'
                        },
                        {
                            external_id: localStorage.getItem("customerId")
                        }
                    );


                    hasTrackedViewContent.current = true;
                }
                /* ------------------------ GTM Configuration ------------------------ */
            } catch (error) {
                console.error(error);
                setProductData(prev => ({ ...prev, loading: false }));
            }
        };

        fetchProductData();
    }, [id]);


    // Set initial active image
    useEffect(() => {
        if (productData.images && productData.images.length > 0 && !activeImage) {
            setActiveImage(productData.images[0]);
        }
    }, [activeImage, productData.images]);

    // Update active image based on selected variant
    useEffect(() => {
        if (selectedColor && productData.images) {
            const variantImage = productData.images.find(img => img.variantId === selectedColor.id);
            if (variantImage) {
                setActiveImage(variantImage);
            } else {
                const defaultImage = productData.images.find(img => !img.variantId) || productData.images[0];
                if (defaultImage) {
                    setActiveImage(defaultImage);
                }
            }
        } else {
            if (productData.images && productData.images.length > 0) {
                const defaultImage = productData.images.find(img => !img.variantId) || productData.images[0];
                if (defaultImage && activeImage?.id !== defaultImage.id) {
                    setActiveImage(defaultImage);
                }
            }
        }
    }, [selectedColor, productData.images]);

    // Update selected variant when selection changes
    useEffect(() => {
        if (!productData.hasVariants) {
            // Non-variant products
            if (productData.colors && productData.colors.length > 0) {
                const defaultColor = productData.colors[0];
                setSelectedVariant({
                    id: defaultColor.id,
                    name: defaultColor.name,
                    quantity: quantity
                });
            }
        } else {
            // Variant products
            if (hasColorVariants && selectedColor) {
                setSelectedVariant({
                    id: selectedColor.id,
                    name: selectedColor.name,
                    quantity: quantity
                });
            } else if (hasSizeVariants && selectedSize) {
                setSelectedVariant({
                    id: selectedSize.id,
                    name: selectedSize.name,
                    quantity: quantity
                });
            } else {
                setSelectedVariant(null);
            }
        }
    }, [selectedColor, selectedSize, quantity, productData.hasVariants, productData.colors, hasColorVariants, hasSizeVariants]);

    // Handle color selection
    const handleColorSelect = (color) => {
        if (hasColorVariants) {
            setSelectedColor(color);
            setBlinkSelector(false);
            setShakeSelector(false);
        }
    };

    // Handle size selection
    const handleSizeSelect = (size) => {
        if (hasSizeVariants) {
            setSelectedSize(size);
            setBlinkSelector(false);
            setShakeSelector(false);
        }
    };

    // Scroll controls for image gallery
    const checkScroll = () => {
        if (!scrollRef.current) return;
        const { scrollLeft, scrollWidth, clientWidth, scrollTop, scrollHeight, clientHeight } = scrollRef.current;

        const isDesktop = window.innerWidth >= 768;
        const buffer = 2;

        if (isDesktop) {
            setCanScrollPrev(scrollLeft > buffer);
            setCanScrollNext(scrollLeft + clientWidth < scrollWidth - buffer);
        } else {
            setCanScrollPrev(scrollTop > buffer);
            setCanScrollNext(scrollTop + clientHeight < scrollHeight - buffer);
        }
    };

    useEffect(() => {
        checkScroll();
        window.addEventListener('resize', checkScroll);
        const container = scrollRef.current;
        if (container) {
            container.addEventListener('scroll', checkScroll);
        }
        const timeoutId = setTimeout(checkScroll, 100);
        return () => {
            window.removeEventListener('resize', checkScroll);
            if (container) {
                container.removeEventListener('scroll', checkScroll);
            }
            clearTimeout(timeoutId);
        };
    }, [activeImage]);

    const scroll = (direction) => {
        if (scrollRef.current) {
            const container = scrollRef.current;
            const isDesktop = window.innerWidth >= 768;
            const amount = isDesktop ? 200 : 150;
            const sign = direction === 'next' ? 1 : -1;

            const options = { behavior: 'smooth' };
            if (isDesktop) {
                options.left = sign * amount;
            } else {
                options.top = sign * amount;
            }

            container.scrollBy(options);
        }
    };

    const handleQuantityChange = (delta) => {
        const newQuantity = Math.max(1, quantity + delta);
        setQuantity(newQuantity);
    };

    // Guide user to select variant
    const guideToVariantSelection = () => {
        const needsColor = shouldShowColorSelector && !selectedColor;
        const needsSize = shouldShowSizeSelector && !selectedSize;

        setBlinkSelector(true);
        setShakeSelector(true);

        if (needsColor && colorSelectorRef.current) {
            setTimeout(() => {
                colorSelectorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 100);
        } else if (needsSize && sizeSelectorRef.current) {
            setTimeout(() => {
                sizeSelectorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 100);
        }

        setTimeout(() => {
            setShakeSelector(false);
        }, 600);

        setTimeout(() => {
            setBlinkSelector(false);
        }, 2000);
    };

    const handleGoBack = () => {
        navigate(`/shop`);
    };

    const handleOrderNow = () => {
        if (isReadyToAdd && selectedVariant) {

            const checkoutData = [{
                "id": '1', "name": productData.name, "variant_type": !productData.hasVariants ? "Color" : (productData.colors ? "Color" : "Size"), "variant": selectedVariant.name, "price": productData.discountPrice ? productData.discountPrice : productData.price,
                "originalPrice": productData.price, "quantity": selectedVariant.quantity, "image": productData.images.find(img => img.variantId === selectedVariant.id)?.selectorImage || productData.images[0].selectorImage,
                "productId": productData.id, "variantId": selectedVariant.id
            }];

            setCheckoutItems(checkoutData);

            navigate(`/checkout`);


        } else {
            guideToVariantSelection();
        }
    };

    const handleAddAndContinue = async () => {
        if (!isReadyToAdd || !selectedVariant) return guideToVariantSelection();
        setIsAddingToCart(true);

        const tracking_data = getManualTrackingMetadata();

        try {
            const customerId = localStorage.getItem('customerId');
            if (!customerId) throw new Error('Customer ID not found');

            const payload = {
                customer_id: customerId,
                product_id: productData.id,
                variant_id: selectedVariant.id,
                quantity,
                tracking_data: tracking_data
            };


            const { status, data } = await axios.post(`${BASE_URL}/api/add_to_cart/`, payload);
            console.log(status === 200 ? 'success' : 'API call failed:', data);
            if (status === 200) {
                setShowAddToCartModal(true)

                /* ------------------------ GTM Configuration ------------------------ */
                pushToDataLayer('add_to_cart',
                    {
                        event_id: tracking_data.event_id,
                        value: ((productData.discountPrice || productData.price) * (quantity || 1)).toFixed(2),
                        currency: 'BDT',
                        content_name: productData.name,
                        content_ids: [String(productData.id)],
                        content_type: 'product',
                        num_items: quantity || 1
                    },
                    {
                        external_id: localStorage.getItem("customerId")
                    }
                );
                /* ------------------------ GTM Configuration ------------------------ */

            };
        } catch (error) {
            console.error('Error adding to cart:', error);
        } finally {
            setIsAddingToCart(false);
            initializeCart();
        }
    };

    // Check if product is ready to add to cart
    const isReadyToAdd = productData.hasVariants
        ? (shouldShowColorSelector ? !!selectedColor : true) &&
        (shouldShowSizeSelector ? !!selectedSize : true)
        : true;

    const formatPrice = (price) => {
        if (price === null || price === undefined) return '0.00';
        return typeof price === 'number' ? price.toFixed(2) : '0.00';
    };

    const getImageUrl = (imagePath) => {
        if (!imagePath) return 'https://via.placeholder.com/500';
        return imagePath.startsWith('http') ? imagePath : `${BASE_URL}${imagePath}`;
    };

    if (productData.loading) {
        return (
            <div className="max-w-7xl mx-auto px-1 sm:px-4 pb-[10px] flex justify-center items-center h-96">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading product details...</p>
                </div>
            </div>
        );
    }

    const currentImage = activeImage || (productData.images && productData.images[0]) || {
        mainImage: '',
        selectorImage: '',
        alt: 'Product image',
        id: 'default'
    };


    return (
        <div className="max-w-7xl mx-auto px-1 sm:px-4 pb-[10px]">

            <div className="flex items-center gap-3 pt-4 mb-2">
                <button
                    onClick={handleGoBack}
                    className="flex items-center justify-center min-w-8 min-h-8 rounded-full border border-gray-200 hover:bg-gray-100 text-gray-600 transition-colors"
                >
                    <ArrowLeft className="w-4 h-4" />
                </button>
                <div className="h-5 w-px bg-gray-200 mx-1"></div>
                <nav className="flex items-center text-sm font-medium text-gray-500">
                    <span className="hover:text-gray-900 cursor-pointer">Shop</span>
                    <span className="mx-2 text-gray-300">/</span>
                    <span className="text-gray-900 font-semibold line-clamp-1">
                        {productData.name || 'Product Name'}
                    </span>
                </nav>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 lg:gap-12 pt-[10px]">
                <div className="md:col-span-6 lg:col-span-6">
                    <div className="w-full select-none max-w-[500px] mx-auto">
                        <div className="flex flex-row md:flex-col gap-3 items-stretch">
                            <div className="flex-1 min-w-0 relative w-full">
                                <div className="aspect-square w-full bg-gray-50 rounded-xl overflow-hidden relative border border-gray-100">
                                    <img
                                        src={getImageUrl(currentImage.mainImage)}
                                        alt={currentImage.alt}
                                        className="w-full h-full object-cover"
                                    />
                                    {productData.badges && productData.badges.length > 0 && (
                                        <div className="absolute top-3 left-3">
                                            <span className="bg-white/90 backdrop-blur-sm text-[10px] font-bold px-2 py-1 rounded shadow-sm uppercase tracking-wider text-black">
                                                {productData.badges[0]}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="relative flex-none w-16 md:w-full md:h-20">
                                <div className="absolute inset-0 md:static flex flex-col md:flex-row items-center justify-center gap-2">
                                    <button
                                        onClick={() => scroll('prev')}
                                        disabled={!canScrollPrev}
                                        className={`flex-none p-1.5 rounded-full bg-white border border-gray-200 shadow-sm text-gray-700 hover:bg-gray-50 hover:text-black transition-all z-10 ${canScrollPrev ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'} h-8 w-8 flex items-center justify-center`}
                                    >
                                        <ChevronUp className="w-4 h-4 block md:hidden" />
                                        <ChevronLeft className="w-4 h-4 hidden md:block" />
                                    </button>

                                    <div
                                        ref={scrollRef}
                                        className="flex-1 flex flex-col md:flex-row gap-2 overflow-auto scroll-smooth w-full h-full min-h-0 min-w-0 items-center md:items-stretch [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
                                    >
                                        {productData.images && productData.images.map((img) => (
                                            <button
                                                key={img.id}
                                                onClick={() => setActiveImage(img)}
                                                className={`relative flex-shrink-0 rounded-md overflow-hidden border transition-all duration-200 w-12 h-12 md:w-16 md:h-16 ${currentImage.id === img.id ? 'border-black ring-1 ring-black opacity-100' : 'border-transparent hover:border-gray-300 opacity-60 hover:opacity-100'}`}
                                            >
                                                <img
                                                    src={getImageUrl(img.selectorImage)}
                                                    alt={img.alt}
                                                    className="w-full h-full object-cover"
                                                />
                                            </button>
                                        ))}
                                    </div>

                                    <button
                                        onClick={() => scroll('next')}
                                        disabled={!canScrollNext}
                                        className={`flex-none p-1.5 rounded-full bg-white border border-gray-200 shadow-sm text-gray-700 hover:bg-gray-50 hover:text-black transition-all z-10 ${canScrollNext ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'} h-8 w-8 flex items-center justify-center`}
                                    >
                                        <ChevronDown className="w-4 h-4 block md:hidden" />
                                        <ChevronRight className="w-4 h-4 hidden md:block" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="md:col-span-6 lg:col-span-6 lg:mr-3">
                    <div className="flex flex-col h-full md:sticky md:top-24">
                        <div className="mb-5">
                            <div className="flex justify-between items-start mb-2">
                                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 line-clamp-1 sm:line-clamp-2">
                                    {productData.name || 'Product Name'}
                                </h1>
                            </div>

                            <div className="flex items-center gap-4 mb-3">
                                <div className="flex items-baseline gap-2">
                                    {productData.discountPrice ? (
                                        <>
                                            <span className="text-xl font-bold text-gray-900">
                                                {productData.currency}{formatPrice(productData.discountPrice)}
                                            </span>
                                            <span className="text-base text-gray-400 line-through">
                                                {productData.currency}{formatPrice(productData.price)}
                                            </span>
                                        </>
                                    ) : (
                                        <span className="text-xl font-bold text-gray-900">
                                            {productData.currency}{formatPrice(productData.price)}
                                        </span>
                                    )}
                                </div>
                                <div className="h-4 w-px bg-gray-200"></div>
                                <div className="flex items-center">
                                    <div className="flex text-yellow-400">
                                        <Star className="w-3.5 h-3.5 fill-current" />
                                    </div>
                                    <span className="ml-1.5 text-sm font-medium text-gray-600">
                                        {productData.rating || 0} <span className="text-gray-400">({productData.reviewCount || 0})</span>
                                    </span>
                                </div>
                            </div>

                            <p className="text-sm text-gray-600 leading-relaxed line-clamp-2">
                                {productData.description || 'Product description'}
                            </p>
                        </div>

                        <div className="space-y-6">
                            {/* Color Selector - Show only if product has color variants */}
                            {shouldShowColorSelector && (
                                <div ref={colorSelectorRef} className={`${blinkSelector && !selectedColor ? 'animate-blink' : ''} ${shakeSelector && !selectedColor ? 'animate-shake' : ''}`}>
                                    <span className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-2 block">
                                        Color: <span className="text-gray-500 font-normal normal-case">{selectedColor ? selectedColor.name : 'Select a Color'}</span>
                                    </span>
                                    <div className="flex flex-wrap items-center gap-2">
                                        {productData.colors.map((color, index) => {
                                            const isOutOfStock = !color.inStock;
                                            const isFirst = index === 0;

                                            return (
                                                <div key={color.id} className="relative group">
                                                    <button
                                                        onClick={() => handleColorSelect(color)}
                                                        disabled={isOutOfStock}
                                                        style={{ backgroundColor: color.value }}
                                                        className={`
                                                            w-8 h-8 rounded-full border-2 transition-all shadow-sm flex items-center justify-center relative overflow-hidden
                                                            ${selectedColor?.id === color.id
                                                                ? 'ring-2 ring-offset-1 ring-black scale-105'
                                                                : 'hover:ring-2 hover:ring-offset-1 hover:ring-gray-300'
                                                            }
                                                            ${isOutOfStock ? 'opacity-60 cursor-not-allowed ring-0' : ''}
                                                        `}
                                                        aria-label={color.name}
                                                    >
                                                        {selectedColor?.id === color.id && !isOutOfStock && (
                                                            <Check className={`w-4 h-4 ${color.value === '#e5e7eb' || color.value === '#ffffff' ? 'text-gray-800' : 'text-white'}`} />
                                                        )}

                                                        {isOutOfStock && (
                                                            <div className="absolute inset-0 flex items-center justify-center">
                                                                <div className="w-[120%] h-[1.5px] bg-gray-500 rotate-45"></div>
                                                            </div>
                                                        )}
                                                    </button>

                                                    {isOutOfStock && (
                                                        <div className={`
                                                            absolute bottom-full mb-2 w-max invisible opacity-0 group-hover:visible group-hover:opacity-100 group-active:visible group-active:opacity-100 transition-all duration-200 z-50
                                                            ${isFirst ? 'left-0' : 'left-1/2 -translate-x-1/2'}
                                                        `}>
                                                            <div className="bg-black text-white text-[10px] font-bold uppercase tracking-wider py-1.5 px-3 rounded shadow-lg relative">
                                                                Not available
                                                                <div className={`
                                                                    absolute top-full border-4 border-transparent border-t-black
                                                                    ${isFirst ? 'left-3' : 'left-1/2 -translate-x-1/2'}
                                                                `}></div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Size Selector - Show only if product has size variants (and no color variants) */}
                            {shouldShowSizeSelector && (
                                <div ref={sizeSelectorRef} className={`${blinkSelector && !selectedSize ? 'animate-blink' : ''} ${shakeSelector && !selectedSize ? 'animate-shake' : ''}`}>
                                    <span className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-2 block">
                                        Size: <span className="text-gray-500 font-normal normal-case">{selectedSize ? selectedSize.name : 'Select a Size'}</span>
                                    </span>
                                    <div className="flex flex-wrap items-center gap-2">
                                        {productData.sizes.map((size, index) => {
                                            const isOutOfStock = !size.inStock;
                                            const isFirst = index === 0;

                                            return (
                                                <div key={size.id} className="relative group">
                                                    <button
                                                        onClick={() => handleSizeSelect(size)}
                                                        disabled={isOutOfStock}
                                                        className={`
                                                            px-4 py-2 rounded-lg text-sm font-medium transition-all relative border overflow-hidden
                                                            ${selectedSize?.id === size.id
                                                                ? 'bg-black text-white border-black'
                                                                : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'
                                                            }
                                                            ${isOutOfStock ? 'opacity-60 cursor-not-allowed bg-gray-100 text-gray-400 border-dashed border-gray-300' : ''}
                                                        `}
                                                        aria-label={size.name}
                                                    >
                                                        <span className={isOutOfStock ? 'decoration-gray-400' : ''}>
                                                            {size.name}
                                                        </span>

                                                        {isOutOfStock && (
                                                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                                                <div className="w-[120%] h-[1.5px] bg-gray-400 rotate-45"></div>
                                                            </div>
                                                        )}
                                                    </button>

                                                    {isOutOfStock && (
                                                        <div className={`
                                                            absolute bottom-full mb-2 w-max invisible opacity-0 group-hover:visible group-hover:opacity-100 group-active:visible group-active:opacity-100 transition-all duration-200 z-50
                                                            ${isFirst ? 'left-0' : 'left-1/2 -translate-x-1/2'}
                                                        `}>
                                                            <div className="bg-black text-white text-[10px] font-bold uppercase tracking-wider py-1.5 px-3 rounded shadow-lg relative">
                                                                Not available
                                                                <div className={`
                                                                    absolute top-full border-4 border-transparent border-t-black
                                                                    ${isFirst ? 'left-4' : 'left-1/2 -translate-x-1/2'}
                                                                `}></div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            )}

                            <Box className="space-y-3 pt-2 border-t border-gray-50" sx={{ display: { xs: 'none', sm: 'block' } }}>
                                <div className="flex gap-3">
                                    <div className="flex items-center justify-between w-24 border border-gray-300 rounded-lg">
                                        <button onClick={() => handleQuantityChange(-1)} className="p-2 text-gray-500 hover:text-black">
                                            <Minus className="w-3 h-3" />
                                        </button>
                                        <span className="text-sm font-medium">{quantity}</span>
                                        <button onClick={() => handleQuantityChange(1)} className="p-2 text-gray-500 hover:text-black">
                                            <Plus className="w-3 h-3" />
                                        </button>
                                    </div>

                                    <button
                                        onClick={handleOrderNow}
                                        className="flex-1 text-white text-sm font-bold tracking-wide py-3 px-4 rounded-lg transition-all shadow-lg flex items-center justify-center gap-2 group bg-black hover:bg-gray-800 hover:shadow-xl"
                                    >
                                        Order Now
                                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                    </button>
                                </div>

                                <button
                                    onClick={handleAddAndContinue}
                                    className={`w-full border border-gray-300 text-black text-sm font-medium py-2.5 rounded-lg flex items-center justify-center gap-2 
                                                hover:bg-gray-50 transition-colors ${isAddingToCart ? 'cursor-not-allowed opacity-70' : ''}`}
                                    disabled={isAddingToCart}
                                >
                                    {isAddingToCart && (
                                        <svg
                                            className="animate-spin h-4 w-4 text-black"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                        >
                                            <circle
                                                className="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="4"
                                            ></circle>
                                            <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                            ></path>
                                        </svg>
                                    )}
                                    {isAddingToCart ? "Adding..." : "Add to cart"}
                                </button>
                            </Box>

                            {/* <SocialButtons /> */}







                            <div className="flex flex-col sm:flex-row md:flex-col lg:flex-row gap-3 mt-4">
                                <div className="flex-1 bg-green-50 p-4 rounded-lg border border-green-100">
                                    <div className="flex items-center gap-2 mb-2 text-green-800 font-bold text-sm uppercase">
                                        <Truck className="w-4 h-4" />
                                        Fast Delivery
                                    </div>
                                    <ul className="text-sm text-green-700 space-y-1 list-disc list-inside pl-1">
                                        <li>Delivery in 2-3 business days</li>
                                        <li>Order by 2 PM for same-day</li>
                                    </ul>
                                </div>

                                <div className="flex-1 bg-blue-50 p-4 rounded-lg border border-blue-100">
                                    <div className="flex items-center gap-2 mb-2 text-blue-800 font-bold text-sm uppercase">
                                        <RotateCcw className="w-4 h-4" />
                                        Easy Returns
                                    </div>
                                    <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside pl-1">
                                        <li>30-day return policy</li>
                                        <li>Pre-paid return label</li>
                                    </ul>
                                </div>
                            </div>

                            {productData.features && productData.features.length > 0 && (
                                <div className="pt-4 border-t border-gray-100">
                                    <h3 className="text-base font-bold text-gray-900 mb-3 uppercase tracking-wider">
                                        Product Highlights
                                    </h3>
                                    <ul className="space-y-2">
                                        {productData.features.map((feature, index) => (
                                            <li key={index} className="flex items-start text-sm text-gray-700 leading-relaxed">
                                                <Check className="w-4 h-4 mr-2 mt-0.5 text-black flex-shrink-0" />
                                                {feature}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                        </div>
                    </div>

                </div>


            </div>

            <RelatedProduct productId={id} />

            <div className="fixed bottom-0 left-0 w-full bg-white border-t border-gray-200 z-50 md:hidden pb-[env(safe-area-inset-bottom)]">
                <CheckoutQuantityCartNav
                    quantity={quantity}
                    onQuantityChange={handleQuantityChange}
                    onOrderNow={handleOrderNow}
                    isAddingToCart={isAddingToCart}
                    onAddToCart={handleAddAndContinue}
                />
            </div>


            <style jsx>{`
                @keyframes blink {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.5; }
                }
                
                @keyframes shake {
                    0%, 100% { transform: translateX(0); }
                    10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
                    20%, 40%, 60%, 80% { transform: translateX(5px); }
                }
                
                .animate-blink {
                    animation: blink 0.5s ease-in-out 4;
                }
                
                .animate-shake {
                    animation: shake 0.6s ease-in-out;
                }
            `}</style>
        </div>
    );
};

// Mobile Bottom Navigation Component
const CheckoutQuantityCartNav = ({ quantity, onQuantityChange, onOrderNow, isAddingToCart, onAddToCart }) => {
    const increment = () => onQuantityChange(1);
    const decrement = () => onQuantityChange(-1);

    return (
        <nav className="flex justify-around items-center w-full h-[60px]">
            <div className="flex items-center gap-3 w-full h-10 px-4">
                <div className="flex items-center bg-gray-100 rounded-lg p-0.5 h-full w-[100px] flex-shrink-0">
                    <button
                        onClick={decrement}
                        disabled={quantity <= 1}
                        className="w-8 h-full flex items-center justify-center text-gray-500 hover:bg-white hover:shadow-sm rounded-md transition-all disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:shadow-none"
                    >
                        <Minus size={14} strokeWidth={2.5} />
                    </button>
                    <span className="flex-1 text-center font-bold text-gray-900 text-sm select-none">{quantity}</span>
                    <button
                        onClick={increment}
                        className="w-8 h-full flex items-center justify-center text-gray-500 hover:bg-white hover:shadow-sm rounded-md transition-all"
                    >
                        <Plus size={14} strokeWidth={2.5} />
                    </button>
                </div>

                <button
                    onClick={onOrderNow}
                    className="flex-1 h-full bg-gray-900 text-white rounded-lg flex items-center justify-center gap-2 shadow-lg shadow-gray-200 active:scale-[0.98] transition-all hover:bg-black"
                >
                    <span className="text-sm font-bold tracking-wide">Order Now</span>
                </button>

                <div className="w-[40px] h-full bg-gray-100 border border-gray-200 rounded-lg flex items-center justify-center" onClick={onAddToCart}>
                    {isAddingToCart ? (
                        <svg
                            className="animate-spin h-4 w-4 text-black"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                            ></circle>
                            <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                            ></path>
                        </svg>
                    ) : (
                        <CartIcon className="h-5 w-5" />
                    )}
                </div>
            </div>
        </nav>
    );
};

export default ProductSection;



